import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Text, Box, Flex } from "@chakra-ui/react";
import { PurchaseButton } from "./PurchaseButton";
import { t } from "i18next";
import { memo, SyntheticEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { theme } from "../../../theme/theme";
import { SavedPaymentMethods } from "./SavedPaymentMethods";
import { RadioOnIcon } from "../../icons/RadioOnIcon";
import { getStripePromise } from "../../../App";
import { OwnerPaymentServiceTokenTypes } from "../types";
import { TestCreditCardWidget } from "./TestCreditCardWidget";
import { useMobileBreakpoint } from "../../../utils/useBreakpoints";

export type StripePaymentConfigTypes = {
  ownerPaymentServiceTokenId?: string;
  stripeToken?: string;
};

export const STRIPE_FORM_ID = "stripe-checkout-form";

// Check if we should use the CI-friendly test widget instead of Stripe iframe
const useTestWidget = (): boolean => {
  return typeof window !== "undefined" && !!window.gc.global.stripeCiWidget;
};

interface StripePaymentProps {
  submitButtonText?: string;
  completePurchase: (paymentConfig: StripePaymentConfigTypes) => void;
  waiting?: boolean;
  failedCard?: OwnerPaymentServiceTokenTypes;
  responseError?: string;
  isDisabled?: boolean;
  isGiftCard?: boolean;
  triggerFocusPayment?: number;
  paddingBottom?: string;
  useFixedFooter?: boolean;
  formId?: string;
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

export const StripePayment = memo(
  ({
    submitButtonText,
    completePurchase,
    waiting,
    responseError,
    failedCard,
    isDisabled = false,
    triggerFocusPayment = 0,
    paddingBottom,
    useFixedFooter = false,
    formId,
    onCanSubmitChange,
  }: StripePaymentProps) => {
    // Phase 2 moved web subscriptions to hosted Stripe Checkout. This component
    // stays in place for legacy saved-card and one-time purchase flows.
    return (
      <Elements
        stripe={getStripePromise()}
        options={{
          fonts: [
            {
              cssSrc:
                "https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap",
            },
          ],
        }}
      >
        <StripeCheckoutForm
          waiting={waiting}
          submitButtonText={submitButtonText}
          completePurchase={completePurchase}
          failedCard={failedCard}
          responseError={responseError}
          isDisabled={isDisabled}
          triggerFocusPayment={triggerFocusPayment}
          paddingBottom={paddingBottom}
          useFixedFooter={useFixedFooter}
          formId={formId}
          onCanSubmitChange={onCanSubmitChange}
        />
      </Elements>
    );
  },
);

export function StripeCheckoutForm({
  submitButtonText,
  completePurchase,
  waiting,
  responseError,
  failedCard,
  isDisabled,
  isGiftCard,
  triggerFocusPayment = 0,
  paddingBottom,
  useFixedFooter = false,
  formId,
  onCanSubmitChange,
}: StripePaymentProps) {
  const isMobile = useMobileBreakpoint();
  const hasInlineButton = !formId;
  const [isReady, setIsReady] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [ownerPaymentServiceTokenId, setOwnerPaymentServiceTokenId] =
    useState<string>();

  const canSubmit =
    (!!ownerPaymentServiceTokenId && ownerPaymentServiceTokenId !== "0") ||
    cardComplete;

  useEffect(() => {
    onCanSubmitChange?.(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  useEffect(() => {
    if (ownerPaymentServiceTokenId !== "0") {
      setCardComplete(false);
    }
  }, [ownerPaymentServiceTokenId]);

  useEffect(() => {
    if (failedCard) {
      setOwnerPaymentServiceTokenId(failedCard.id.toString());
    }
  }, [failedCard]);

  const { loading, setLoading, error, setError, handleSubmit, focusPayment } =
    useStripePayment(completePurchase);

  useEffect(() => {
    if (triggerFocusPayment > 0) {
      focusPayment();
    }
  }, [triggerFocusPayment]);

  const [autoFocus, setAutoFocus] = useState(false);
  useEffect(() => {
    if (ownerPaymentServiceTokenId && ownerPaymentServiceTokenId !== "0") {
      setLoading(false);
      setAutoFocus(true);
    }
  }, [ownerPaymentServiceTokenId]);

  useEffect(() => {
    setError(responseError);
  }, [responseError]);

  // Extract themes so I can pass them into the stipe config
  // which doesn't know our themes
  const errorColor = theme.colors.error["300"];
  const textColor = theme.colors.dune["600"];
  return (
    <form
      id={formId}
      style={{
        width: "100%",
        borderRadius: "16px",
      }}
      onSubmit={(e) => {
        if (ownerPaymentServiceTokenId === "0") {
          handleSubmit(e);
        } else {
          e.preventDefault();
          completePurchase({ ownerPaymentServiceTokenId });
        }
      }}
    >
      <Flex
        flexDirection="column"
        gap="16px"
        pb={hasInlineButton ? (paddingBottom ?? (isMobile ? "16px" : "32px")) : undefined}
      >
        {/* Saved cards and inline card entry still back legacy web purchase flows.
            Subscription modals no longer render this component for Stripe Billing. */}
        <SavedPaymentMethods
          setOwnerPaymentServiceTokenId={(newOwnerPaymentServiceTokenId) => {
            setIsReady(true);
            setOwnerPaymentServiceTokenId(newOwnerPaymentServiceTokenId);
          }}
          ownerPaymentServiceTokenId={ownerPaymentServiceTokenId}
          onMethodChange={() => setError(undefined)}
        />
        {ownerPaymentServiceTokenId === "0" && (
          <Box
            display="flex"
            alignItems="center"
            height={isMobile ? "40px" : undefined}
            paddingY={!isMobile ? "12px" : undefined}
            paddingX="16px"
            bg="transparent.white.10"
            borderRadius="16px"
            border="1px solid"
            boxShadow={
              error
                ? "0px 0px 5px 0px var(--chakra-colors-error-300)"
                : undefined
            }
            borderColor={error ? "error.300" : "transparent"}
          >
            <Box
              alignItems="center"
              height={!isMobile ? "24px" : undefined}
              width="36px"
              display="inline-block"
            >
              <RadioOnIcon height="24px" width="24px" color="blaze.blaze" />
            </Box>
            <Box
              display="inline-block"
              width="calc(100% - 36px)"
              height={isMobile ? undefined : "100%"}
            >
              {useTestWidget() ? (
                <TestCreditCardWidget
                  onChange={(e) => {
                    e.error ? setError(e.error.message) : setError(undefined);
                    setCardComplete(!!e.complete);
                  }}
                  onReady={(element) => {
                    setIsReady(true);
                    setLoading(false);
                    if (autoFocus) element.focus();
                  }}
                  disabled={waiting || loading}
                />
              ) : (
                <CardElement
                  onChange={(e) => {
                    e.error ? setError(e.error.message) : setError(undefined);
                    // Stripe's e.complete occasionally reports true after
                    // browser autocomplete fills card/expiry/CVC but leaves
                    // the postal code empty. Cross-check the value so we
                    // don't enable the purchase button prematurely.
                    const hasPostalCode = !!e.value?.postalCode?.trim();
                    setCardComplete(e.complete && hasPostalCode);
                  }}
                  onReady={(element) => {
                    setIsReady(true);
                    setLoading(false);
                    if (autoFocus) element.focus();
                  }}
                  options={{
                    disableLink: true,
                    style: {
                      base: {
                        fontSize: "14px",
                        fontWeight: "500",
                        fontFamily: "Roboto",
                        color: textColor,
                        "::placeholder": {
                          color: textColor,
                        },
                      },
                      invalid: {
                        color: errorColor,
                        iconColor: errorColor,
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </Flex>
      <Box width="100%" height={error ? "24px" : "0px"} pl="8px">
        {error && (
          <Text
            color="error.300"
            fontFamily="Roboto"
            fontSize="14px"
            lineHeight="130%"
          >
            {error}
          </Text>
        )}
      </Box>
      {hasInlineButton && useFixedFooter
        ? (() => {
            const scrollContainer =
              document.getElementById("gcModalScrollContainer") ||
              document.getElementById("slideInMenuScrollContainer");
            if (!scrollContainer) return null;

            return createPortal(
              <Box
                position="sticky"
                bottom="0"
                left="0"
                right="0"
                p="24px"
                borderTop="1px solid"
                borderColor="transparent.white.20"
                backdropFilter="blur(18px)"
                background="#141517CC"
                boxShadow="0px 4px 24px 0px rgba(0, 0, 0, 0.40)"
                zIndex={100}
                pointerEvents="auto"
              >
                <PurchaseButton
                  isDisabled={isDisabled || waiting || loading || !isReady}
                  text={
                    submitButtonText ||
                    t(
                      isGiftCard
                        ? "components.payments.subscription.confirmGiftCardPayment"
                        : "components.payments.subscription.confirmYourPayment",
                    )
                  }
                  loading={waiting || loading}
                  onClick={(e) => {
                    if (ownerPaymentServiceTokenId === "0") {
                      handleSubmit(e);
                    } else {
                      e.preventDefault();
                      completePurchase({ ownerPaymentServiceTokenId });
                    }
                  }}
                />
              </Box>,
              scrollContainer,
            );
          })()
        : null}
      {hasInlineButton && !useFixedFooter && (
        <PurchaseButton
          isDisabled={isDisabled || waiting || loading || !isReady}
          text={
            submitButtonText ||
            t(
              isGiftCard
                ? "components.payments.subscription.confirmGiftCardPayment"
                : "components.payments.subscription.confirmYourPayment",
            )
          }
          loading={waiting || loading}
        />
      )}
    </form>
  );
}

function useStripePayment(
  completePurchase: ({
    stripeToken,
    ownerPaymentServiceTokenId,
  }: StripePaymentConfigTypes) => void,
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const submittingRef = useRef(false);

  const stripe = useStripe();
  const elements = useElements();

  const focusPayment = () => {
    elements?.getElement(CardElement)?.focus();
  };

  const handleSubmit = async (
    event: SyntheticEvent<HTMLFormElement | HTMLButtonElement>,
  ) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;

    // In test widget mode, generate a test token directly without calling Stripe API
    if (useTestWidget()) {
      setLoading(true);
      const cardNumberInput = document.querySelector<HTMLInputElement>(
        '[data-elements-stable-field-name="cardNumber"]',
      );
      const cardNumber = cardNumberInput?.value.replace(/\s/g, "") || "";

      // Map test card numbers to Stripe test tokens
      const testTokenMap: Record<string, string> = {
        "4242424242424242": "tok_visa",
        "4000056655665556": "tok_visa_debit",
        "5555555555554444": "tok_mastercard",
        "2223003122003222": "tok_mastercard",
        "5200828282828210": "tok_mastercard_debit",
        "378282246310005": "tok_amex",
        "6011111111111117": "tok_discover",
        "3056930009020004": "tok_diners",
        "30569309025904": "tok_diners",
        "6200000000000005": "tok_unionpay",
        "3566002020360505": "tok_jcb",
        // Error test cards
        "4000000000000002": "tok_chargeDeclined",
        "4000000000009995": "tok_chargeDeclinedInsufficientFunds",
        "4000000000009987": "tok_chargeDeclinedLostCard",
        "4000000000009979": "tok_chargeDeclinedStolenCard",
        "4000000000000069": "tok_chargeDeclinedExpiredCard",
        "4000000000000127": "tok_chargeDeclinedIncorrectCvc",
        "4000000000000119": "tok_chargeDeclinedProcessingError",
        "4242424242424241": "tok_chargeCustomerFail",
      };

      const testToken = testTokenMap[cardNumber] || "tok_visa";

      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 300));

      completePurchase({ stripeToken: testToken });
      return;
    }

    // Production mode: use real Stripe - require stripe and elements
    if (!stripe || !elements) return;

    setLoading(true);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    const { error, token } = await stripe.createToken(cardElement);

    if (error) {
      setError(error.message);
      submittingRef.current = false;
    } else {
      //cardElement.clear();
      completePurchase({ stripeToken: token.id });
    }
  };

  useEffect(() => {
    if (error) {
      setLoading(false);
      submittingRef.current = false;
    }
  }, [error]);

  useEffect(() => {
    if (loading) {
      setError(undefined);
    } else {
      setLoading(false);
    }
  }, [loading]);

  return {
    loading,
    setLoading,
    error,
    setError,
    handleSubmit,
    focusPayment,
  };
}
